import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client.ts";
import type { Brand } from "../api/types.ts";
import { confirmAction, toast } from "../components/feedback.tsx";
import { RetireActions, ShowArchived } from "../components/Retire.tsx";
import { Card, Empty, TableSkeleton } from "../components/ui.tsx";

interface TemplateRow {
  id: string;
  name: string;
  subject: string;
  /** The subject with merge fields resolved — what a recipient actually sees. */
  subjectPreview: string;
  mode: "blocks" | "raw" | "html";
  archivedAt: string | null;
  updatedAt: string;
}

interface Imported {
  id: string;
  name: string;
  warnings: string[];
}

export function TemplatesPage() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const templates = useQuery({
    queryKey: ["templates", showArchived],
    queryFn: () => api.get<TemplateRow[]>(`/templates${showArchived ? "?archived=true" : ""}`),
  });

  const refresh = () => {
    setError(null);
    void qc.invalidateQueries({ queryKey: ["templates"] });
  };

  /**
   * Refused while a campaign or sequence uses it — which is most of them once
   * used, since a sent campaign's template is the record of what went out.
   * Archiving is how one of those gets retired.
   */
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/templates/${id}`),
    onSuccess: () => {
      refresh();
      toast("Template deleted");
    },
    onError: (err: Error) => setError(`Couldn't delete: ${err.message}. Archive it instead.`),
  });
  const archive = useMutation({
    mutationFn: (id: string) => api.post(`/templates/${id}/archive`),
    onSuccess: () => {
      refresh();
      toast("Template archived");
    },
    onError: (err: Error) => setError(err.message),
  });
  const unarchive = useMutation({
    mutationFn: (id: string) => api.post(`/templates/${id}/unarchive`),
    onSuccess: () => {
      refresh();
      toast("Template restored");
    },
    onError: (err: Error) => setError(err.message),
  });
  const pending = remove.isPending || archive.isPending || unarchive.isPending;

  return (
    <div className="page">
      <div className="page-header">
        <h1>Templates</h1>
        <p>Compiled once on save, then merged per recipient at send time.</p>
      </div>

      <div className="toolbar">
        <Link className="btn" to="/templates/new">
          + New template
        </Link>
        <button type="button" className="btn ghost" onClick={() => setImporting((v) => !v)}>
          {importing ? "Cancel" : "Import HTML"}
        </button>
        <ShowArchived value={showArchived} onChange={setShowArchived} />
      </div>

      {importing && <ImportHtml onDone={() => setImporting(false)} />}

      {error && <div className="error">{error}</div>}

      <Card>
        {templates.isLoading ? (
          <TableSkeleton />
        ) : templates.data?.length === 0 ? (
          <Empty>
            No templates yet.{" "}
            <Link className="btn small" to="/templates/new">
              + New template
            </Link>
          </Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Template</th>
                <th>Subject</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {templates.data?.map((t) => (
                <tr key={t.id}>
                  <td>
                    <Link to={`/templates/${t.id}`} style={{ fontWeight: 600 }}>
                      {t.name}
                    </Link>
                    {t.mode === "raw" && (
                      <span className="pill warn" style={{ marginLeft: 8 }}>
                        MJML
                      </span>
                    )}
                    {t.mode === "html" && (
                      <span className="pill info" style={{ marginLeft: 8 }}>
                        HTML
                      </span>
                    )}
                  </td>
                  <td style={{ color: "var(--gray-500)" }}>
                    {t.subjectPreview?.trim() ? (
                      t.subjectPreview
                    ) : (
                      <span style={{ fontStyle: "italic", color: "var(--gray-400)" }}>
                        No subject
                      </span>
                    )}
                    {/* The raw line too when they differ, so it is clear a
                        merge field is involved rather than a literal name. */}
                    {t.subjectPreview !== t.subject && (
                      <div className="mono" style={{ fontSize: 10.5, color: "var(--gray-400)" }}>
                        {t.subject}
                      </div>
                    )}
                  </td>
                  <td className="mono">{new Date(t.updatedAt).toLocaleDateString()}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <RetireActions
                      archived={t.archivedAt !== null}
                      pending={pending}
                      onArchive={() => archive.mutate(t.id)}
                      onUnarchive={() => unarchive.mutate(t.id)}
                      onDelete={async () => {
                        if (
                          await confirmAction({
                            title: `Delete ${t.name}?`,
                            message: "This can't be undone.",
                            confirmLabel: "Delete template",
                            destructive: true,
                          })
                        )
                          remove.mutate(t.id);
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

/**
 * Import a finished HTML email.
 *
 * The file is read in the browser and sent as text rather than as a multipart
 * upload: the API stores a string, and a one-field form is not worth a second
 * content type on the route.
 *
 * Deliberately does not navigate away on success. The import reports what it
 * found — merge fields with no fallback, a missing unsubscribe link, a document
 * Gmail will clip — and redirecting straight into the editor would throw that
 * away at the one moment it is worth reading.
 */
function ImportHtml({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const [brandId, setBrandId] = useState("");
  const [name, setName] = useState("");
  const [html, setHtml] = useState("");
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Imported | null>(null);

  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () => api.get<Brand[]>("/config/brands"),
  });

  useEffect(() => {
    if (!brandId && brands.data?.[0]) setBrandId(brands.data[0].id);
  }, [brandId, brands.data]);

  const importIt = useMutation({
    mutationFn: () =>
      api.post<Imported>("/templates/import", {
        brandId,
        html,
        ...(name.trim() ? { name: name.trim() } : {}),
      }),
    onSuccess: (made) => {
      setError(null);
      setResult(made);
      void qc.invalidateQueries({ queryKey: ["templates"] });
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Card title="Import HTML">
      <div className="card-body">
        {error && <div className="error">{error}</div>}

        {result ? (
          <>
            <div className="notice">
              Imported <strong>{result.name}</strong>. It will be sent exactly as written.
            </div>
            {result.warnings.map((w) => (
              <div className="notice warn" key={w}>
                {w}
              </div>
            ))}
            <div className="toolbar" style={{ marginBottom: 0, marginTop: 10 }}>
              <Link className="btn" to={`/templates/${result.id}`}>
                Open it
              </Link>
              <button type="button" className="btn ghost" onClick={onDone}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="row">
              <div className="field">
                <label htmlFor="i-brand">Brand</label>
                <select id="i-brand" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
                  {brands.data?.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="i-name">Name</label>
                <input
                  id="i-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="From the file's <title>"
                />
              </div>
            </div>

            <div className="field">
              <label htmlFor="i-file">HTML file</label>
              <input
                id="i-file"
                type="file"
                accept=".html,.htm,text/html"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setHtml(await file.text());
                  setFileName(file.name);
                }}
              />
              <div className="hint">
                {fileName
                  ? `${fileName} — ${(new Blob([html]).size / 1024).toFixed(1)}KB`
                  : "The whole document, head and all. Nothing is recompiled, so what you upload is what goes out."}
              </div>
            </div>

            <button
              type="button"
              className="btn"
              disabled={!html || !brandId || importIt.isPending}
              onClick={() => importIt.mutate()}
            >
              {importIt.isPending ? "Importing…" : "Import"}
            </button>
          </>
        )}
      </div>
    </Card>
  );
}
